import { unref } from 'vue'
import type { MaybeRef } from './usePolicyTypeSelection'

export type DateTimeFormatType = 'date' | 'time' | 'datetime'

const FORMAT_DATE_DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
}

const FORMAT_TIME_DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
}

const FORMAT_DATE_TIME_DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
  ...FORMAT_DATE_DEFAULT_OPTIONS,
  ...FORMAT_TIME_DEFAULT_OPTIONS,
}

const toDate = (value: string | Date): Date | undefined => {
  if (typeof value === 'string' && value.length > 0) {
    const isDateOnly = !value.includes('T')
    return new Date(isDateOnly ? `${value}T00:00:00` : value)
  }
  if (value instanceof Date) {
    return value
  }
}

const isValidDate = (date: Date): date is Date => !isNaN(date.getTime())

/**
 * Composable for formatting dates and times using Intl.DateTimeFormat with i18n support.
 *
 * Returns standardized methods to format dates, times, and date-times according to the current locale, as well as a customizable formatting method for unique use cases.
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 */
export function useDateTimeFormat() {
  // Customizable date-time formatting method - use ONLY for unique use cases, the default methods below should cover most needs!
  const formatDateTimeCustom = (
    value?: MaybeRef<string | Date | null | undefined>,
    options: Intl.DateTimeFormatOptions,
    fallbackValue: string
  ) => {
    const rawValue = unref(value)
    if (!rawValue) return fallbackValue

    const date = toDate(rawValue)
    if (date && !isValidDate(date)) return fallbackValue

    return new Intl.DateTimeFormat(undefined, options).format(date)
  }

  // Standardized methods for date-time formatting - use this unless you have a unique use case
  const formatDateTime = (value?: MaybeRef<string | Date | null>) => {
    return formatDateTimeCustom(
      value,
      FORMAT_DATE_TIME_DEFAULT_OPTIONS,
      '- -:-'
    )
  }

  // Standardized method for date formatting - use this unless you have a unique use case
  const formatDate = (value?: MaybeRef<string | Date | null>) => {
    return formatDateTimeCustom(value, FORMAT_DATE_DEFAULT_OPTIONS, '-')
  }

  // Standardized method for time formatting - use this unless you have a unique use case
  const formatTime = (value?: MaybeRef<string | Date | null>) => {
    return formatDateTimeCustom(value, FORMAT_TIME_DEFAULT_OPTIONS, '-:-')
  }

  return { formatDateTime, formatDate, formatTime, formatDateTimeCustom }
}
